import { differenceInCalendarDays, parseISO } from 'date-fns';
import {
  HormonePhaseSummary,
  HormoneStatistics,
  HormoneSummaryResponse,
} from '../../model/dto/hormone-summary';
import { UserInfo } from '../../model/dto/user-info';
import {
  CycleLengthStatus,
  CycleVariabilityStatus,
  EffectiveHormoneMaxima,
  HormoneExpectedRange,
  HormoneLevelStatus,
  HormonePhaseViewModel,
  HormonePatternMetricViewModel,
  HormoneStabilityStatus,
  HormoneSummaryViewModel,
  OvulationMagnitudeStatus,
  OvulationStatus,
  PhaseLengthStatus,
} from '../../model/type/hormone-summary-status';

export const HORMONE_SUMMARY_DEFAULT_MAXIMA: EffectiveHormoneMaxima = {
  lh: 150,
  pg: 22.5,
  lr_e3g: 500,
};

export const HORMONE_SUMMARY_THRESHOLD_FRACTIONS = {
  pg: {
    follicular: {
      lowBelow: 3 / 22.5,
      elevatedAbove: 5 / 22.5,
    },
    luteal: {
      lowBelow: 5 / 22.5,
      elevatedAbove: Infinity,
    },
  },
  lr_e3g: {
    follicular: {
      lowBelow: 75 / 500,
      elevatedAbove: 150 / 500,
    },
    luteal: {
      lowBelow: 100 / 500,
      elevatedAbove: 250 / 500,
    },
  },
  lh: {
    surge: 0.1666666666,
    magnitudeElevatedAbove: 75 / 150,
  },
} as const;

type PatientHormoneMaxima = Pick<
  UserInfo,
  'personal_max_lh' | 'personal_max_pg' | 'personal_max_lr_e3g'
>;

type HormonePattern = 'pg' | 'lr_e3g';
type CyclePhase = 'follicular' | 'luteal';

export function getEffectiveHormoneMaxima(
  patient: PatientHormoneMaxima
): EffectiveHormoneMaxima {
  return {
    lh: patient.personal_max_lh ?? HORMONE_SUMMARY_DEFAULT_MAXIMA.lh,
    pg: patient.personal_max_pg ?? HORMONE_SUMMARY_DEFAULT_MAXIMA.pg,
    lr_e3g:
      patient.personal_max_lr_e3g ?? HORMONE_SUMMARY_DEFAULT_MAXIMA.lr_e3g,
  };
}

export function getHormoneExpectedRange(
  hormone: HormonePattern,
  phase: CyclePhase,
  effectiveMaxima: EffectiveHormoneMaxima
): HormoneExpectedRange {
  const fractions = HORMONE_SUMMARY_THRESHOLD_FRACTIONS[hormone][phase];
  const effectiveMaximum = effectiveMaxima[hormone];

  return {
    low: effectiveMaximum * fractions.lowBelow,
    high: effectiveMaximum * fractions.elevatedAbove,
  };
}

export function getHormoneLevelStatus(
  median: number | null,
  lowBelow: number,
  elevatedAbove: number
): HormoneLevelStatus {
  if (median === null) {
    return 'insufficient_data';
  }

  if (median < lowBelow) {
    return 'low';
  }

  if (median > elevatedAbove) {
    return 'elevated';
  }

  return 'expected';
}

export function getHormoneStabilityStatus(
  percentOfDaysInExpectedRange: number | null
): HormoneStabilityStatus {
  if (percentOfDaysInExpectedRange === null) {
    return 'insufficient_data';
  }

  return percentOfDaysInExpectedRange >= 50 ? 'expected' : 'variable';
}

export function getOvulationStatus(isOvulatoryCycle: boolean): OvulationStatus {
  return isOvulatoryCycle ? 'ovulated' : 'anovulatory';
}

export function getOvulationMagnitudeStatus(
  lhSurgeValue: number | null,
  lhSurgeThreshold: number,
  lhElevatedThreshold: number
): OvulationMagnitudeStatus {
  if (lhSurgeValue === null) {
    return 'insufficient_data';
  }

  if (lhSurgeValue < lhSurgeThreshold) {
    return 'low';
  }

  if (lhSurgeValue > lhElevatedThreshold) {
    return 'elevated';
  }

  return 'adequate';
}

export function getLhSurgeCycleDay(
  summary: HormoneSummaryResponse
): number | null {
  if (summary.lh_surge === null) {
    return null;
  }

  return (
    differenceInCalendarDays(
      parseISO(summary.lh_surge.day),
      parseISO(summary.start_day)
    ) + 1
  );
}

export function getFollicularPhaseLengthStatus(
  lengthDays: number | null
): PhaseLengthStatus {
  if (lengthDays === null) {
    return 'insufficient_data';
  }

  if (lengthDays < 10) {
    return 'short';
  }

  if (lengthDays > 17) {
    return 'long';
  }

  return 'expected';
}

export function getLutealPhaseLengthStatus(
  lengthDays: number | null
): PhaseLengthStatus {
  if (lengthDays === null) {
    return 'insufficient_data';
  }

  if (lengthDays < 12) {
    return 'short';
  }

  if (lengthDays > 16) {
    return 'long';
  }

  return 'expected';
}

export function getCycleLengthStatus(
  lengthDays: number | null
): CycleLengthStatus {
  if (lengthDays === null) {
    return 'insufficient_data';
  }

  if (lengthDays > 90) {
    return 'extended';
  }

  if (lengthDays < 22) {
    return 'short';
  }

  if (lengthDays > 35) {
    return 'long';
  }

  return 'expected';
}

export function getCycleVariabilityStatus(
  valueDays: number | null
): CycleVariabilityStatus {
  if (valueDays === null) {
    return 'insufficient_data';
  }

  return valueDays > 7 ? 'variable' : 'expected';
}

function mapHormonePatternMetric(
  statistics: HormoneStatistics,
  expectedRange: HormoneExpectedRange
): HormonePatternMetricViewModel {
  return {
    levelStatus: getHormoneLevelStatus(
      statistics.median,
      expectedRange.low,
      expectedRange.high
    ),
    stabilityStatus: getHormoneStabilityStatus(
      statistics.percent_of_days_in_expected_range
    ),
    median: statistics.median,
    measurementDayCount: statistics.measurement_day_count,
    percentOfDaysInExpectedRange: statistics.percent_of_days_in_expected_range,
    expectedRange,
  };
}

function mapHormonePhase(
  phaseSummary: HormonePhaseSummary | null,
  phase: CyclePhase,
  effectiveMaxima: EffectiveHormoneMaxima
): HormonePhaseViewModel | null {
  if (phaseSummary === null) {
    return null;
  }

  return {
    lengthDays: phaseSummary.length_days,
    pg: mapHormonePatternMetric(
      phaseSummary.pg,
      getHormoneExpectedRange('pg', phase, effectiveMaxima)
    ),
    lrE3g: mapHormonePatternMetric(
      phaseSummary.lr_e3g,
      getHormoneExpectedRange('lr_e3g', phase, effectiveMaxima)
    ),
  };
}

export function applyInsufficientScanDataOverride(
  viewModel: HormoneSummaryViewModel
): HormoneSummaryViewModel {
  return {
    ...viewModel,
    follicularPhase: null,
    cycleOverview: {
      ...viewModel.cycleOverview,
      ovulationStatus: 'insufficient_data',
      ovulationMagnitudeStatus: 'insufficient_data',
      lhSurgeCycleDay: null,
      follicularPhaseLengthDays: null,
      follicularPhaseLengthStatus: 'insufficient_data',
    },
  };
}

export function mapHormoneSummaryToViewModel(
  summary: HormoneSummaryResponse,
  patient: PatientHormoneMaxima
): HormoneSummaryViewModel {
  const effectiveMaxima = getEffectiveHormoneMaxima(patient);
  const lhSurgeThreshold =
    effectiveMaxima.lh * HORMONE_SUMMARY_THRESHOLD_FRACTIONS.lh.surge;
  const lhElevatedThreshold =
    effectiveMaxima.lh *
    HORMONE_SUMMARY_THRESHOLD_FRACTIONS.lh.magnitudeElevatedAbove;
  const follicularPhaseLengthDays =
    summary.follicular_phase?.length_days ?? null;
  const lutealPhaseLengthDays = summary.luteal_phase?.length_days ?? null;

  return {
    cycleId: summary.cycle_id,
    startDay: summary.start_day,
    endDay: summary.end_day,
    effectiveMaxima,
    follicularPhase: mapHormonePhase(
      summary.follicular_phase,
      'follicular',
      effectiveMaxima
    ),
    lutealPhase: mapHormonePhase(
      summary.luteal_phase,
      'luteal',
      effectiveMaxima
    ),
    cycleOverview: {
      isOvulatoryCycle: summary.is_ovulatory_cycle,
      ovulationStatus: getOvulationStatus(summary.is_ovulatory_cycle),
      ovulationMagnitudeStatus: getOvulationMagnitudeStatus(
        summary.lh_surge?.value ?? null,
        lhSurgeThreshold,
        lhElevatedThreshold
      ),
      lhSurgeCycleDay: getLhSurgeCycleDay(summary),
      lhSurgeValue: summary.lh_surge?.value ?? null,
      pgConfirmationWindowDays: summary.pg_confirmation_window_days,
      pgMaxInConfirmationWindow: summary.pg_max_in_confirmation_window,
      follicularPhaseLengthDays,
      follicularPhaseLengthStatus: getFollicularPhaseLengthStatus(
        follicularPhaseLengthDays
      ),
      lutealPhaseLengthDays,
      lutealPhaseLengthStatus: getLutealPhaseLengthStatus(
        lutealPhaseLengthDays
      ),
      cycleLengthDays: summary.cycle_length_days,
      cycleLengthStatus: getCycleLengthStatus(summary.cycle_length_days),
      cycleLengthVariabilityDays: summary.cycle_length_variability_days,
      cycleLengthVariabilityStatus: getCycleVariabilityStatus(
        summary.cycle_length_variability_days
      ),
    },
  };
}
